import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { submitPrive, submitCollective } from "../lib/api";
import { DESTINATIONS } from "../data/content";

const ease = [0.16, 1, 0.3, 1];
const field = "w-full bg-transparent border-b border-navy/25 py-3 text-navy placeholder:text-charcoal/40 focus:border-gold outline-none transition-colors";
const labelCls = "overline text-charcoal/50 mb-2 block";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function Chips({ options, value, onChange, testid }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button type="button" key={o} onClick={() => toggle(o)}
          className={`px-4 py-2 text-sm border transition-colors ${value.includes(o) ? "border-gold bg-gold text-navy" : "border-navy/25 text-charcoal/70 hover:border-gold"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

function Success({ onReset }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16" data-testid="enquiry-success">
      <div className="h-16 w-16 rounded-full bg-gold text-navy flex items-center justify-center mx-auto"><Check size={28} /></div>
      <h3 className="font-serif text-4xl text-navy mt-8">Thank you.</h3>
      <p className="text-charcoal/70 max-w-md mx-auto mt-4 leading-relaxed">
        Your enquiry has reached our travel designers. A dedicated curator will be in touch shortly to begin crafting your journey.
      </p>
      <button onClick={onReset} className="btn-ghost text-navy mt-10">Submit another enquiry</button>
    </motion.div>
  );
}

function PriveForm({ initialDestination }) {
  const [f, setF] = useState({
    full_name: "", title: "", mobile: "", email: "", destination: initialDestination || "",
    travel_date: "", duration: "", adults: "", children: "", child_ages: "", occasion: "",
    hotel_category: [], contact_method: "", special_requests: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name || !f.mobile || !f.email || !f.destination) { toast.error("Please complete the required fields."); return; }
    setLoading(true);
    try { await submitPrive(f); setDone(true); toast.success("Enquiry sent."); }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  if (done) return <Success onReset={() => { setDone(false); setF({ ...f, special_requests: "" }); }} />;

  return (
    <form onSubmit={submit} className="space-y-12" data-testid="prive-form">
      <div>
        <p className="font-serif text-2xl text-navy mb-6">Personal Details</p>
        <div className="grid md:grid-cols-2 gap-8">
          <div><label className={labelCls}>Full Name *</label><input data-testid="prive-name" className={field} value={f.full_name} onChange={set("full_name")} /></div>
          <div><label className={labelCls}>Title</label>
            <select className={field} value={f.title} onChange={set("title")}><option value="">Select</option>{["Mr.", "Mrs.", "Ms.", "Dr."].map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label className={labelCls}>Mobile Number *</label><input data-testid="prive-mobile" className={field} value={f.mobile} onChange={set("mobile")} /></div>
          <div><label className={labelCls}>Email Address *</label><input data-testid="prive-email" type="email" className={field} value={f.email} onChange={set("email")} /></div>
        </div>
      </div>

      <div>
        <p className="font-serif text-2xl text-navy mb-6">Your Journey</p>
        <div className="grid md:grid-cols-2 gap-8">
          <div><label className={labelCls}>Destination *</label>
            <input data-testid="prive-destination" list="dest-list" className={field} value={f.destination} onChange={set("destination")} />
            <datalist id="dest-list">{DESTINATIONS.map((d) => <option key={d.slug} value={d.name} />)}</datalist>
          </div>
          <div><label className={labelCls}>Preferred Travel Date *</label><input type="date" className={field} value={f.travel_date} onChange={set("travel_date")} /></div>
          <div><label className={labelCls}>Journey Duration</label><input className={field} placeholder="e.g. 7 nights" value={f.duration} onChange={set("duration")} /></div>
          <div><label className={labelCls}>Travel Occasion</label>
            <select className={field} value={f.occasion} onChange={set("occasion")}><option value="">Select</option>{["Honeymoon", "Anniversary", "Family Holiday", "Luxury Escape", "Business & Leisure", "Friends Getaway", "Adventure", "Celebration", "Other"].map((o) => <option key={o}>{o}</option>)}</select></div>
          <div><label className={labelCls}>Number of Adults</label><input className={field} value={f.adults} onChange={set("adults")} /></div>
          <div><label className={labelCls}>Number of Children</label><input className={field} value={f.children} onChange={set("children")} /></div>
        </div>
        <div className="mt-8">
          <label className={labelCls}>Preferred Hotel Category</label>
          <Chips testid="prive-hotel-cat" options={["4★ Premium", "5★ Luxury", "Boutique Luxury", "Private Villa", "Luxury Resort"]} value={f.hotel_category} onChange={(v) => setF({ ...f, hotel_category: v })} />
        </div>
        <div className="mt-8">
          <label className={labelCls}>Preferred Contact Method</label>
          <select className={field} value={f.contact_method} onChange={set("contact_method")}><option value="">Select</option>{["Phone Call", "WhatsApp", "Email"].map((o) => <option key={o}>{o}</option>)}</select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Special Requests</label>
        <textarea data-testid="prive-message" rows={4} className={field} placeholder="Tell us about your dream journey, preferred experiences, celebrations, or any special requirements." value={f.special_requests} onChange={set("special_requests")} />
      </div>

      <button type="submit" disabled={loading} data-testid="prive-submit" className="btn-gold">
        {loading ? "Sending…" : "Request a Bespoke Itinerary"} <ArrowRight size={16} />
      </button>
    </form>
  );
}

function CollectiveForm() {
  const [f, setF] = useState({
    organization: "", contact_person: "", mobile: "", email: "", travel_category: "",
    destination: "", travel_date: "", duration: "", total_travellers: "", accommodation: [], message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.organization || !f.contact_person || !f.mobile || !f.email || !f.destination) { toast.error("Please complete the required fields."); return; }
    setLoading(true);
    try { await submitCollective(f); setDone(true); toast.success("Enquiry sent."); }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  if (done) return <Success onReset={() => { setDone(false); setF({ ...f, message: "" }); }} />;

  return (
    <form onSubmit={submit} className="space-y-12" data-testid="collective-form">
      <div className="grid md:grid-cols-2 gap-8">
        <div><label className={labelCls}>Organization / Institution *</label><input data-testid="collective-org" className={field} value={f.organization} onChange={set("organization")} /></div>
        <div><label className={labelCls}>Contact Person *</label><input data-testid="collective-person" className={field} value={f.contact_person} onChange={set("contact_person")} /></div>
        <div><label className={labelCls}>Mobile Number *</label><input data-testid="collective-mobile" className={field} value={f.mobile} onChange={set("mobile")} /></div>
        <div><label className={labelCls}>Email Address *</label><input data-testid="collective-email" type="email" className={field} value={f.email} onChange={set("email")} /></div>
        <div><label className={labelCls}>Travel Category</label>
          <select className={field} value={f.travel_category} onChange={set("travel_category")}><option value="">Select</option>{["Corporate Travel", "MICE", "Educational Tour", "School Excursion", "College Industrial Visit", "Sports Team Travel", "Religious Group", "Family Group", "Friends Group", "Senior Citizen Tour", "Destination Wedding Guests", "NGO/Association", "Government Delegation", "Other"].map((o) => <option key={o}>{o}</option>)}</select></div>
        <div><label className={labelCls}>Destination *</label><input data-testid="collective-destination" className={field} value={f.destination} onChange={set("destination")} /></div>
        <div><label className={labelCls}>Preferred Travel Date</label><input type="date" className={field} value={f.travel_date} onChange={set("travel_date")} /></div>
        <div><label className={labelCls}>Journey Duration</label><input className={field} placeholder="e.g. 5 days" value={f.duration} onChange={set("duration")} /></div>
        <div><label className={labelCls}>Total Travellers</label><input className={field} placeholder="Adults / Children" value={f.total_travellers} onChange={set("total_travellers")} /></div>
      </div>
      <div>
        <label className={labelCls}>Accommodation Preference</label>
        <Chips testid="collective-accom" options={["3★ Hotel", "4★ Premium", "5★ Luxury", "Business Hotel", "Resort", "Villa", "Twin Sharing", "Triple Sharing", "Dormitory", "Mixed"]} value={f.accommodation} onChange={(v) => setF({ ...f, accommodation: v })} />
      </div>
      <div>
        <label className={labelCls}>Your Requirements</label>
        <textarea data-testid="collective-message" rows={4} className={field} placeholder="Tell us about your travel objectives, event schedule, transportation, dietary needs or any specific expectations." value={f.message} onChange={set("message")} />
      </div>
      <button type="submit" disabled={loading} data-testid="collective-submit" className="btn-gold">
        {loading ? "Sending…" : "Request a Customized Proposal"} <ArrowRight size={16} />
      </button>
    </form>
  );
}

export default function Enquiry() {
  const q = useQuery();
  const navigate = useNavigate();
  const initialType = q.get("type");
  const destination = q.get("destination") || "";
  const [tab, setTab] = useState(initialType === "collective" ? "collective" : initialType === "prive" ? "prive" : null);

  const heading = useMemo(() => tab === "collective"
    ? "Whether a corporate delegation, educational tour or large group, our specialists craft seamless end-to-end solutions."
    : "Whether a secluded island, an alpine escape or an expedition beyond the ordinary, our curators craft it exclusively for you.", [tab]);

  return (
    <main data-testid="enquiry-page" className="bg-ivory min-h-screen">
      <div className="h-[76px] bg-navy" />
      <section className="bg-navy grain pt-16 pb-20">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="overline text-gold mb-6">Enquire</p>
            <h1 className="font-serif text-ivory text-5xl md:text-7xl leading-[1.02]">It would be our privilege<br />to craft your journey.</h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[900px] mx-auto px-6 md:px-10 py-16">
        {!tab && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="enquiry-selector">
            {[
              { k: "prive", t: "Privé", d: "Bespoke private & luxury travel." },
              { k: "collective", t: "Collective", d: "Group, corporate & institutional travel." },
            ].map((o) => (
              <button key={o.k} onClick={() => setTab(o.k)} data-testid={`select-${o.k}`}
                className="group border border-navy/15 p-10 text-left hover:border-gold transition-colors bg-white/50">
                <h3 className="font-serif text-4xl text-navy group-hover:text-gold transition-colors">{o.t}</h3>
                <p className="text-charcoal/60 mt-3">{o.d}</p>
                <span className="inline-flex items-center gap-2 overline text-gold mt-6">Continue <ArrowRight size={14} /></span>
              </button>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {tab && (
            <motion.div key={tab} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.6, ease }}>
              <div className="flex items-center gap-6 mb-10">
                <div className="flex border border-navy/15">
                  {["prive", "collective"].map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                      className={`px-6 py-3 overline transition-colors ${tab === t ? "bg-navy text-ivory" : "text-charcoal/60"}`}>
                      {t === "prive" ? "Privé" : "Collective"}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-charcoal/70 leading-relaxed mb-12 max-w-2xl">{heading}</p>
              {tab === "prive" ? <PriveForm initialDestination={destination} /> : <CollectiveForm />}
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </main>
  );
}
